import { Observable, Subject } from 'rxjs';

import { NotificationDismissReason } from './models/notification.enum';
import { NotificationInstance } from './models/notification.model';

/**
 * Handle to a live notification, returned by every `NotificationService` call.
 *
 * ```ts
 * const ref = this.notify.error('Upload failed.', 'Server error.', {
 *   action: notificationAction(NotificationActionPreset.Retry),
 * });
 * ref.onAction().subscribe(() => this.upload());
 * ref.afterDismissed().subscribe((reason) => console.log(reason));
 * ```
 */
export class NotificationRef {
  private readonly action = new Subject<NotificationInstance>();
  private readonly dismissal = new Subject<NotificationDismissReason>();
  private settled = false;

  constructor(
    /** Id of the notification this handle controls. */
    readonly id: string,
    private readonly dismissFn: (id: string) => void
  ) {}

  /** Emits every time the action button is pressed. */
  onAction(): Observable<NotificationInstance> {
    return this.action.asObservable();
  }

  /** Emits once with the reason the toast left the screen, then completes. */
  afterDismissed(): Observable<NotificationDismissReason> {
    return this.dismissal.asObservable();
  }

  /** Closes the toast early. No-op once it has already gone. */
  dismiss(): void {
    if (!this.settled) {
      this.dismissFn(this.id);
    }
  }

  /** @internal Called by `NotificationService` when the action fires. */
  _emitAction(notification: NotificationInstance): void {
    if (!this.settled) {
      this.action.next(notification);
    }
  }

  /** @internal Called by `NotificationService` once the toast is removed. */
  _settle(reason: NotificationDismissReason): void {
    if (this.settled) {
      return;
    }
    this.settled = true;
    this.dismissal.next(reason);
    this.dismissal.complete();
    this.action.complete();
  }
}
